import { useEffect, useRef, useState } from "react";
import { StickyNote, Check } from "lucide-react";
import { Textarea } from "../ui/textarea";

const MAX_LEN = 4000;

/**
 * Free-form student notes — a single textarea that auto-saves on blur.
 *
 * Design choices (kept deliberately simple per Phase E spec):
 *   • Single text field, not a timestamped log. "Phase A" of student notes.
 *   • Auto-saves on blur (no Save button needed). Shows a brief "saved" chip
 *     after a successful write so teachers know it landed.
 *   • Read-only on archived students — they can still see notes for context
 *     but the textarea is disabled. (Restoring re-enables editing.)
 *   • Hard cap at 4000 characters — gives a clear feedback when nearing the
 *     limit without surprising the teacher with hidden truncation.
 */
export function StudentNotesCard({
    student,
    onSave,
    disabled = false,
}) {
    const [value, setValue] = useState(student.notes || "");
    const [savedFlash, setSavedFlash] = useState(false);
    const savedTimer = useRef(null);
    const lastSaved = useRef(student.notes || "");

    // Sync if the student changes (e.g. navigated via ←/→). Guard against
    // the effect firing as a side-effect of our own save: if the incoming
    // notes match what we just persisted, there's nothing to resync and we
    // must NOT reset the "Saved" chip.
    useEffect(() => {
        const incoming = student.notes || "";
        if (incoming === lastSaved.current) return;
        setValue(incoming);
        lastSaved.current = incoming;
        setSavedFlash(false);
    }, [student.id, student.notes]);

    useEffect(
        () => () => {
            if (savedTimer.current) clearTimeout(savedTimer.current);
        },
        [],
    );

    const persist = () => {
        if (disabled) return;
        const next = value.slice(0, MAX_LEN);
        if (next === lastSaved.current) return;
        onSave(next);
        lastSaved.current = next;
        setSavedFlash(true);
        if (savedTimer.current) clearTimeout(savedTimer.current);
        savedTimer.current = setTimeout(() => setSavedFlash(false), 1500);
    };

    const remaining = MAX_LEN - value.length;
    const nearLimit = remaining < 300;
    const hasNotes = value.trim().length > 0;

    return (
        <div
            className="bg-white rounded-3xl border border-mist card-soft p-6 sm:p-8"
            data-testid="student-notes-card"
        >
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-warning-soft text-warning flex items-center justify-center">
                        <StickyNote size={18} strokeWidth={2.5} />
                    </div>
                    <div>
                        <h2 className="font-display font-bold text-xl uppercase tracking-tight">
                            Notes
                        </h2>
                        <p className="text-xs text-slate-500">
                            Quiet context only you see — preferences, parents, reminders.
                        </p>
                    </div>
                </div>
                {savedFlash && (
                    <span
                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] text-success bg-success-soft border border-success-soft px-2 py-1 rounded-full"
                        data-testid="student-notes-saved-chip"
                    >
                        <Check size={11} strokeWidth={3} />
                        Saved
                    </span>
                )}
            </div>

            <Textarea
                value={value}
                onChange={(e) => setValue(e.target.value.slice(0, MAX_LEN))}
                onBlur={persist}
                disabled={disabled}
                placeholder={
                    disabled
                        ? "No notes."
                        : "e.g. Left-handed · sensitive to volume · parent prefers WhatsApp · likes minor keys…"
                }
                rows={5}
                className="rounded-xl bg-mist/50 border-mist min-h-[120px] focus-visible:ring-[#3D6385] resize-y"
                data-testid="student-notes-input"
                aria-label="Student notes"
            />

            <div className="mt-2 flex items-center justify-between text-[11px] text-slate-400">
                <span>
                    {disabled
                        ? "Read-only · restore the student to edit."
                        : hasNotes
                            ? "Saves automatically when you click away."
                            : "Saves automatically. Click away when done."}
                </span>
                <span
                    className={`tabular-nums font-bold ${nearLimit ? "text-warning" : "text-slate-400"}`}
                    data-testid="student-notes-remaining"
                >
                    {remaining}
                </span>
            </div>
        </div>
    );
}
